import { createContext, useContext, useEffect, useState } from 'react';
import useAuditLogsActions from '../hooks/APIHooks/users/useAuditLogsActions';
import useFeatureActions from '../hooks/APIHooks/users/useFeatureActions';
import { AuditLog } from '../types/auditLogs';
import { formatFeatureName } from '../utils/commonFunctions';

// datatype of the context declared here
interface IAuditLogContext {
  auditLog: AuditLog | undefined;
  auditLogList: Array<AuditLog>;
  filterList: Array<string>;
  selectedFilter: string;
  showLogDetails: boolean;
  setAuditLog: React.Dispatch<React.SetStateAction<AuditLog | undefined>>;
  setSelectedFilter: React.Dispatch<React.SetStateAction<string>>;
  setShowLogDetails: React.Dispatch<React.SetStateAction<boolean>>;
  isFetching: boolean;
  isLoading: boolean;
}

// creating the context here
const AuditLogContext = createContext<IAuditLogContext | null>(null);

const AuditLogProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [showLogDetails, setShowLogDetails] = useState<boolean>(false);

  const [auditLogList, setAuditLogList] = useState<Array<AuditLog>>([]);
  const [auditLog, setAuditLog] = useState<AuditLog>();

  const [filterList, setFilterList] = useState<Array<string>>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>('Users');

  // integration of custom API hooks here
  const { getAuditLogsHandler } = useAuditLogsActions(
    selectedFilter.split(' ').join('')
  );
  const { getFeatureListHandler } = useFeatureActions('getFeatureList');

  console.log('AUDIT LOGS: ', getAuditLogsHandler.data?.data);
  console.log('FEATURE LIST [AUDIT LOGS]: ', getFeatureListHandler.data?.data);

  // setting features into filter list local state here
  useEffect(() => {
    if (getFeatureListHandler.data?.is_success) {
      setFilterList(
        getFeatureListHandler.data.data.map((feature) =>
          formatFeatureName(feature.name)
        )
      );
    }
  }, [getFeatureListHandler.data]);

  // setting audit logs into local state here
  useEffect(() => {
    if (getAuditLogsHandler.data?.is_success) {
      setAuditLogList(getAuditLogsHandler.data.data);
    }
  }, [getAuditLogsHandler.data]);

  // refetching audit logs on changing the model name here
  useEffect(() => {
    setAuditLogList([]);

    if (selectedFilter) {
      getAuditLogsHandler.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  // rendering the context provider here
  return (
    <AuditLogContext.Provider
      value={{
        auditLogList,
        filterList,
        selectedFilter,
        showLogDetails,
        auditLog,
        setShowLogDetails,
        setAuditLog,
        setSelectedFilter,
        isFetching: getAuditLogsHandler.isFetching,
        isLoading: getAuditLogsHandler.isLoading,
      }}
    >
      {children}
    </AuditLogContext.Provider>
  );
};

export default AuditLogProvider;

// custom hook to access audit log context declared here
export const useAuditLogContext = () => {
  const context = useContext(AuditLogContext);

  if (!context) {
    throw new Error('useAuditLogContext must be within AuditLogProvider!');
  }

  return context;
};
